import { COOKIE_DOMAIN } from 'Settings';

export const capitalize = (str) =>
  str.substr(0, 1).toUpperCase() + str.substr(1);

const pluralizableStrings = {
  student: 'students',
  teacher: 'teachers',
  invite: 'invites',
};

export const pluralize = (str) => {
  if (!pluralizableStrings[str]) {
    console.debug(
      `The string "${str}" was not in the pluralizableStrings array, so an s was added to the end.`
    );
    return `${str}s`;
  }
  return pluralizableStrings[str];
};

export const pluralizeByCount = (count, str) => {
  return count !== 1 ? pluralize(str) : str;
};

export const isAnActualObject = (value) => {
  return (
    typeof value === 'object' && // It is an object
    !Array.isArray(value) && // but not an array
    value !== null
  ); // And not null!
};

// This only supports one level of flattening
export const flattenObject = (obj) => {
  const newObj = {};
  const keys = Object.keys(obj);
  const values = Object.values(obj);
  keys.forEach((key, i) => {
    if (!isAnActualObject(values[i])) newObj[key] = values[i];
  });
  keys.forEach((key, i) => {
    if (isAnActualObject(values[i])) {
      Object.assign(newObj, values[i]);
    }
  });

  return newObj;
};

export const objectToArray = (obj) => {
  return Object.keys(obj).map((key) => obj[key]);
};

// Based on https://stackoverflow.com/a/38750895
export const filterObjectByKeys = (input, allowedKeys) => {
  return Object.keys(input)
    .filter((key) => allowedKeys.includes(key))
    .reduce((obj, key) => {
      // eslint-disable-next-line no-param-reassign
      obj[key] = input[key];
      return obj;
    }, {});
};

export const getDateString = (d, options) => {
  // d is a Date.
  const defaultOptions = {
    timeZone: 'America/New_York',
    day: 'numeric',
    month: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  };
  const locale = new Intl.DateTimeFormat('en-US', options || defaultOptions);
  return locale.format(d);
};

export const daysSinceDate = (start) => {
  const diff = (Date.now() - start.getTime()) / 1000;
  return Math.floor(diff / 60 / 60 / 24);
};

export const daysUntilDate = (end, justTheNumber = false) => {
  const diff = (end.getTime() - Date.now()) / 1000;
  // Subtract an extra hundred to avoid it showing as '32 days';
  const days = Math.floor((diff - 100) / 60 / 60 / 24);
  if (justTheNumber) return days;
  return `${days} ${pluralizeByCount(days, 'day')}`;
};

// https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie/Simple_document.cookie_framework
/* eslint-disable no-useless-escape */
export const getCookie = (name) => {
  return (
    decodeURIComponent(
      document.cookie.replace(
        new RegExp(
          `(?:(?:^|.*;)\\s*${encodeURIComponent(name).replace(
            /[\-\.\+\*]/g,
            '\\$&'
          )}\\s*\\=\\s*([^;]*).*$)|^.*$`
        ),
        '$1'
      )
    ) || null
  );
};
/* eslint-enable no-useless-escape */

export const setCookie = (name, value, expirationDate, extraParams = '') => {
  document.cookie = `${name}=${value};domain=${COOKIE_DOMAIN};path=/;expires=${expirationDate.toUTCString()}${extraParams}`;
};

export const deleteCookie = (name) => {
  document.cookie = `${name}=;domain=${COOKIE_DOMAIN};path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT`;
};

export const deleteTinybopDomainTokenCookie = (name) => {
  console.log('deleting tinybop.com cookie');
  document.cookie = `${name}=;domain=.tinybop.com;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT`;
  document.cookie = `${name}=;domain=tinybop.com;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT`;
};

// In every JWT, we have an object that provides the user’s roles,
// ID, token expiration and the sensitive token expiration.
// We almost always need the standard expiration, but you can
// specify the key as 'sxp' to get sensitive token expiration.
export const getExpirationFromToken = (token, key = 'exp') => {
  if (!token) return null;

  const tokenParts = token.split('.');
  if (tokenParts.length <= 1) return null;

  const tokenData = JSON.parse(atob(tokenParts[1]));
  return typeof tokenData === 'object' ? tokenData[key] * 1000 : null;
};

export const getTokenFromCookie = () => {
  return getCookie('tbsToken');
};

export const setTokenCookie = (token) => {
  const expirationDate = new Date(getExpirationFromToken(token));
  setCookie(
    'tbsToken',
    encodeURIComponent(token),
    expirationDate,
    ';SameSite=None;Secure=true;'
  );
};

export const deleteTokenCookie = (passThru = null) => {
  deleteCookie('tbsToken');
  deleteTinybopDomainTokenCookie('tbsToken');
  deleteCookie('tbsActiveSchool');
  return passThru;
};

export const setUserTypeCookie = (value) => {
  const now = new Date();
  const expirationDate = new Date();
  expirationDate.setYear(now.getFullYear() + 1);
  setCookie('tbsUserType', value, expirationDate);
};

export const setPasswordVisibilityCookie = (visible) => {
  const value = visible ? 'visible' : 'hidden';
  const now = new Date();
  const expirationDate = new Date();
  expirationDate.setYear(now.getFullYear() + 1);
  setCookie('tbsPasswordVisibility', value, expirationDate);
};

export const setLocaleCookie = (locale) => {
  const now = new Date();
  const expirationDate = new Date();
  expirationDate.setYear(now.getFullYear() + 1);
  setCookie('tbsLocale', locale, expirationDate);
};

export const shouldFormBeReadOnly = (error) => {
  switch (error.errorCode) {
    case 22:
      // This error occurs when a username is in use.
      // We only want it to be readonly if the user
      // exists in the same school.
      return error.errorMsg[0][0] === 'student_exists';
    default:
      return false;
  }
};

export const getNameString = (user) => {
  const { given_name, family_name } = user;
  let name = given_name;
  if (family_name) name += ` ${family_name}`;
  return name;
};

export const tryToParseJson = (str) => {
  try {
    return { value: JSON.parse(str), valid: true };
  } catch (e) {
    return { value: str, valid: false };
  }
};

// Feature-detecting localStorage
// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Testing_for_availability
export const storageAvailable = (type) => {
  let storage;
  try {
    storage = window[type];
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
};

export const getSessionStorage = (itemId, isJSON = true) => {
  if (storageAvailable('sessionStorage')) {
    const values = window.sessionStorage.getItem(itemId);
    if (!isJSON) return values;

    try {
      return JSON.parse(values);
    } catch {
      return values;
    }
  }
};

export const deleteSessionStorage = (itemId) => {
  if (storageAvailable('sessionStorage')) {
    const item = window.sessionStorage.removeItem(itemId);
    return true;
  }
  return false;
};

export const updateSessionStorageJSON = (itemId, values) => {
  if (storageAvailable('sessionStorage')) {
    const existingValues = getSessionStorage(itemId);
    let updatedValues = {};
    if (existingValues) {
      updatedValues = {
        ...existingValues,
        ...values,
      };
    } else {
      updatedValues = values;
    }
    window.sessionStorage.setItem(itemId, JSON.stringify(updatedValues));
    return updatedValues;
  }
};
